import { PaginatedResponse } from '@gorila-shared-ui/components';
import { atom, selector, selectorFamily } from 'recoil';
import { InternalUser, User } from '../types/user';
import { userPasswordErrors } from '../utils/password';
import { apiParamsState } from './app';
import { userPermissionsDisabledState } from './permissions';

export const loadingUsersState = atom({
  key: 'app.users.loading',
  default: false,
});

export const usersState = atom<PaginatedResponse<User> | undefined>({
  key: 'app.users',
  default: undefined,
});

export const searchUsersState = atom<string | undefined>({
  key: 'app.users.search',
  default: undefined,
});

export const usersPageState = atom({
  key: 'app.users.page',
  default: 1,
});

export const userIdsState = selector<string[] | undefined>({
  key: 'app.userIds',
  get: ({ get }) => {
    const users = get(usersState);
    if (!users) return;
    return users.items.map((user) => user._id);
  },
});

export const userState = selectorFamily<User | undefined, string>({
  key: 'app.user',
  get:
    (id) =>
    ({ get }) => {
      const users = get(usersState);
      if (!users) return;
      return users.items.find((user) => user._id === id);
    },
});

export const selectedUserIdState = atom<string | undefined>({
  key: 'app.users.selected',
  default: undefined,
});

export const editingUserState = atom<InternalUser | undefined>({
  key: 'app.users.editing',
  default: selector({
    key: 'app.users.editing.initializer',
    get: ({ get }) => {
      const selectedUserId = get(selectedUserIdState);
      if (selectedUserId === undefined) return;
      if (selectedUserId === '') {
        const apiParams = get(apiParamsState);
        const permissionsTemplate = get(userPermissionsDisabledState(false));
        if (!apiParams) return;
        return {
          _id: '',
          username: '',
          permissions: permissionsTemplate,
          name: '',
          email: '',
          subAccount: {
            _id: '',
            name: '',
          },
          password: '',
          confirmPassword: '',
        };
      }
      const user = get(userState(selectedUserId));
      if (!user) return
      const {_id, username, permissions, name, email, subAccount} = user
      return {
        _id,
        username,
        permissions,
        name,
        email,
        subAccount
      };
    },
  }),
});

export const updateUsersState = atom<string | undefined>({
  key: 'app.users.update',
  default: undefined,
});

export const changeUserPasswordState = atom<boolean>({
  key: 'app.user.changePassword',
  default: false,
});

export const userValidPasswordState = selector({
  key: 'app.users.editing.validPassword',
  get: ({ get }) => {
    const user = get(editingUserState);
    if (!user) return false;

    if (!!user.password && !!user.confirmPassword) {
      if (!!userPasswordErrors(user, 'errors') || !!userPasswordErrors(user, 'repeatPasswordErrors')) return false;
      return true;
    }
    return false;
  },
});
